import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer } from '@deck.gl/layers';
import { Loader } from '@googlemaps/loader';
import config from '../../config';
import rampColor from '../ramp-color';
import store from '../../store'; // eslint-disable-line
import { setMapLoaded } from '../../reducers/mapRefs';
import { openModal } from '../../reducers/modal';

import createMapOptions from './createMapOptions';

let map;
let overlay;
let locationMarker;
let sourceLayers;

const { google: { mapOptions, GOOGLE_MAPS_API_KEY } } = config;
const darkGray = [32, 32, 32];

const setOpacity = (color, opacity) => {
  const c = color.slice();
  c.push(Math.round(opacity * 255));
  return c;
};

const getRatio = (f, layer) => {
  const r = (f.properties[layer.id] - layer.min.value) / (layer.max.value - layer.min.value);

  if (r < 0) {
    return 0;
  }
  if (r > 1) {
    return 1;
  }

  return r;
};

const getInverseRatio = (f, layer) => 1 - getRatio(f, layer);

const layerClick = (info) => {
  const layer = sourceLayers.find((l) => l.id === info.layer.id);
  let id = null;

  if (layer.group === 'bg') {
    id = `Block Group: ${info.object.properties.ID}`;
  } else {
    id = `Name: ${info.object.properties.name}`;
  }
  const value = info.object.properties[info.layer.id];
  store.dispatch(openModal({ data: { title: layer.name, msg: `${id}\nValue: ${value}${layer.valueUnits}` } }));
  return true;
};

const planClick = (info) => {
  const blockGroup = info.object.properties.ID;
  const summedWeights = sourceLayers.map((layer) => layer.weight).reduce((pv, cv) => pv + cv, 0);
  const value = sourceLayers.map((layer) => getInverseRatio(info.object, layer) * layer.weight).reduce((pv, cv) => pv + cv, 0) / summedWeights;
  const allocation = info.object.properties.tah;
  store.dispatch(openModal({ data: { title: 'Plan', msg: `Block Group: ${blockGroup}\nValue: ${value.toFixed(3)}\nTree Allocation: ${allocation}` } }));
  return true;
};

export const renderLayers = (layers) => {
  sourceLayers = [];
  let displayLayers = [];

  if (layers) {
    sourceLayers = layers.slice().reverse().filter((layer) => layer.show && layer.opacity);

    if (sourceLayers.length) {
      displayLayers = sourceLayers.map((layer) => new GeoJsonLayer({
        id: layer.id,
        data: config.groups[layer.group].source,
        getFillColor: (f) => {
          const r = getRatio(f, layer);
          return setOpacity(rampColor(layer.colors, r), layer.opacity);
        },
        getLineColor: setOpacity(darkGray, layer.opacity),
        getLineWidth: 1,
        lineWidthMinPixels: 1,
        updateTriggers: {
          getFillColor: [layer.opacity],
          getLineColor: [layer.opacity],
        },
        pickable: true,
        onClick: layerClick,
      }));
    }
  }

  overlay.setProps({ layers: displayLayers });
};

export const renderPlan = (plan, layers) => {
  sourceLayers = layers.filter;

  sourceLayers = [];
  let displayLayers = [];

  if (layers) {
    sourceLayers = layers.filter((layer) => layer.group === 'bg' && layer.weight);

    if (sourceLayers.length) {
      const triggers = sourceLayers.map((layer) => layer.weight);
      const summedWeights = triggers.reduce((pv, cv) => pv + cv, 0);
      triggers.push(plan.opacity);

      const planLayer = new GeoJsonLayer({
        id: 'plan',
        data: config.groups[sourceLayers[0].group].source,
        getFillColor: (f) => {
          const r = sourceLayers.map((layer) => getInverseRatio(f, layer) * layer.weight).reduce((pv, cv) => pv + cv, 0) / summedWeights;
          return setOpacity(rampColor(plan.colors, r), plan.opacity);
        },
        getLineColor: setOpacity(darkGray, plan.opacity),
        getLineWidth: 1,
        lineWidthMinPixels: 1,
        updateTriggers: {
          getFillColor: triggers,
          getLineColor: triggers,
        },
        pickable: true,
        onClick: planClick,
      });

      displayLayers = [planLayer];
    }
  }

  overlay.setProps({ layers: displayLayers });
};

export const createMap = async () => {
  // set up Google Maps API loader
  const loader = new Loader({ apiKey: GOOGLE_MAPS_API_KEY, version: 'beta', libraries: ['places'] });
  // trigger loading of Google Maps API
  await loader.load();
  const { google } = window;

  // TODO: get zoom, lat, lng from URL and pass them to createMapOptions

  // set map options
  const mapOptions = createMapOptions();
  map = new google.maps.Map(document.getElementById('map'), mapOptions);

  overlay = new GoogleMapsOverlay({ layers: [] });
  overlay.setMap(map);

  store.dispatch(setMapLoaded(true));
  return map;
};

export const goToLocation = (location) => {
  if (locationMarker) {
    locationMarker.setMap(null);
  }

  if (location.lat) {
    locationMarker = new window.google.maps.Marker({
      position: location,
      map,
      title: location.address,
      zIndex: 2,
    });

    map.setCenter(location);
    map.setZoom(15);
  }
};

export const resetMapView = () => map.setOptions(mapOptions);

export const getMap = () => map;
